import React, { useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import HomeBackground from '../components/HomeBackground';
import { motion, useAnimation } from 'framer-motion';
import Footer from '../components/Footer';
import AdvancedHeroSection from '../components/AdvancedHeroSection';
import AdvancedFeaturesSection from '../components/AdvancedFeaturesSection';
import PolicySection from '../components/PolicySection';
import OwnerSection from '../components/OwnerSection';
import { 
  Sparkles,
  Zap,
  Star,
  Shield,
  Users,
  TrendingUp,
  Globe,
  Award
} from 'lucide-react';

const Home = () => {
  const { loginWithGoogle, loginAsAdmin } = useAuth();
  const controls = useAnimation();
  const containerRef = useRef();

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const x = (clientX - width / 2) / width;
      const y = (clientY - height / 2) / height;
      
      controls.start({
        x: x * 25,
        y: y * 25,
        transition: { duration: 0.8, ease: "easeOut" }
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, [controls]);

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      // Logic điều hướng đã được xử lý trong AuthContext
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleAdminLogin = async () => {
    try {
      await loginAsAdmin();
      // Logic điều hướng đã được xử lý trong AuthContext
    } catch (error) {
      console.error('Admin login failed:', error);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900"
    >
      {/* Three.js Background */}
      <div className="fixed inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
          <HomeBackground />
          <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.2} />
        </Canvas>
      </div>

      {/* Animated Background Particles */}
      <div className="absolute inset-0 z-10">
        {[...Array(60)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -150, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      {/* Floating Orbs */}
      <div className="absolute inset-0 z-10">
        <motion.div
          className="absolute top-20 left-20 w-60 h-60 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-15"
          animate={{
            scale: [1, 1.4, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-40 right-40 w-48 h-48 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-40 left-1/3 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-10"
          animate={{
            scale: [1, 1.5, 1],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-50 bg-gradient-to-r from-gray-900/80 via-blue-900/80 to-purple-900/80 backdrop-blur-xl shadow-2xl border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <motion.div
                  animate={{ 
                    boxShadow: [
                      "0 0 20px rgba(59, 130, 246, 0.5)",
                      "0 0 40px rgba(147, 51, 234, 0.8)",
                      "0 0 20px rgba(59, 130, 246, 0.5)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="inline-block p-2 rounded-lg bg-white/10 backdrop-blur-sm"
                >
                  <img 
                    src="https://res.cloudinary.com/dypjamkif/image/upload/v1754457007/Dvt_Hulo_logo_copy_t6flpb.png" 
                    alt="DVT Hulo Logo" 
                    className="h-10 w-auto"
                  />
                </motion.div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGoogleLogin}
                className="inline-flex items-center px-6 py-2.5 border border-white/20 rounded-lg shadow-lg text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 backdrop-blur-sm"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Đăng nhập
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-20">
        {/* Hero Section */}
        <AdvancedHeroSection 
          onGoogleLogin={handleGoogleLogin}
          onAdminLogin={handleAdminLogin}
        />

        {/* Features Section */}
        <AdvancedFeaturesSection />

        {/* Owner Section */}
        {/* <OwnerSection /> */}

        {/* Policy Section */}
        <PolicySection />
      </main>

      <Footer />
    </div>
  );
};

export default Home; 
